import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from '../../utils/toast';
import { useAuth } from '../../context/auth-provider';
import {
  AppNotification,
  fetchUnreadCount,
  listNotifications,
  markRead as markReadRequest,
  notificationSocketUrl,
} from '../../api/notifications-client';

/**
 * The notification state for the whole app: one list, one unread count, one
 * socket.
 *
 * ONE SOCKET, mounted at the root. The bell in the staff rail and the bell on
 * the resident dashboard are two views of this provider, not two connections —
 * the server caps sockets per user, and a second connection per surface would
 * spend that budget on tabs the user is not even looking at.
 *
 * ---------------------------------------------------------------------------
 * WHY THE LIST IS NOT REFETCHED WHEN A FRAME ARRIVES
 * ---------------------------------------------------------------------------
 * The socket carries the full notification row — the same shape the REST list
 * returns — so an arriving frame is prepended directly. That is safe for a
 * structural reason worth stating: the server keys its socket registry by USER
 * ID, so a socket can only ever receive rows minted for its own user. There is
 * no shared channel and no client-side filtering to get wrong.
 *
 * De-duplication is by id, because the server derives the id of a de-duplicated
 * event from its dedupe key: a redelivered webhook or a re-run sweep produces
 * the SAME id, not a second copy.
 */

interface NotificationsContextValue {
  notifications: AppNotification[];
  unread: number;
  loading: boolean;
  /** Null when there is no further page. */
  nextCursor: string | null;
  /** True while the WebSocket is open — the bell shows a "live" affordance. */
  connected: boolean;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  markRead: (ids: string[]) => Promise<void>;
  markAllRead: () => Promise<void>;
  /**
   * Hear every notification frame as it arrives (Lobbero Stays plan S6: the
   * guest cards refetch when a frame about a stay on screen lands). Called
   * AFTER the row is prepended. Returns the unsubscribe.
   */
  subscribe: (fn: (row: AppNotification) => void) => () => void;
}

export const NotificationsContext = createContext<NotificationsContextValue | undefined>(
  undefined
);

export const useNotifications = (): NotificationsContextValue => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }

  return ctx;
};

const PAGE_SIZE = 20;

/**
 * Reconnect backoff. Starts fast because the common cause is a deploy (the
 * server closes every socket with 1001 and is back within seconds), and caps
 * low enough that a laptop opened after lunch reconnects while the user is
 * still looking at the screen.
 */
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30000;

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { authStatus, user, refreshAuth } = useAuth();
  // A ref, so the socket's handler never has to be rebuilt for it.
  const refreshAuthRef = useRef(refreshAuth);
  refreshAuthRef.current = refreshAuth;
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  const socketRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(RECONNECT_MIN_MS);
  const timerRef = useRef<number | null>(null);
  // Guards every async setState against a component that unmounted (a logout)
  // while a request was still in flight.
  const aliveRef = useRef(true);
  // The frame listeners (plan S6). A ref: subscribing never rebuilds the socket.
  const listenersRef = useRef(new Set<(row: AppNotification) => void>());
  const subscribe = useCallback((fn: (row: AppNotification) => void) => {
    listenersRef.current.add(fn);

    return () => {
      listenersRef.current.delete(fn);
    };
  }, []);

  const authed = authStatus === 'authed' && Boolean(user);

  const refresh = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const page = await listNotifications({ limit: PAGE_SIZE });
      if (!aliveRef.current) return;
      setNotifications(page.notifications);
      setUnread(page.unread);
      setNextCursor(page.next_before);
    } catch {
      // Silent. The bell is ambient: a failed poll must not put an error toast
      // in front of somebody who was doing something else. The badge simply
      // keeps its last known value until the next attempt.
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [authed]);

  const loadMore = useCallback(async () => {
    if (!authed || !nextCursor) return;
    setLoading(true);
    try {
      const page = await listNotifications({
        limit: PAGE_SIZE,
        before: nextCursor,
      });
      if (!aliveRef.current) return;
      setNotifications(current => {
        const seen = new Set(current.map(n => n.id));

        return current.concat(page.notifications.filter(n => !seen.has(n.id)));
      });
      setNextCursor(page.next_before);
    } catch {
      /* see refresh() */
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [authed, nextCursor]);

  const markRead = useCallback(
    async (ids: string[]) => {
      if (!authed || !ids.length) return;
      const stamp = new Date().toISOString();
      // Optimistic: the row greys out the instant it is clicked. The server is
      // idempotent (`read_at IS NULL` in its WHERE), so a re-send after a
      // failure cannot move a timestamp that is already set.
      setNotifications(current =>
        current.map(n => (ids.includes(n.id) && !n.read_at ? { ...n, read_at: stamp } : n))
      );
      try {
        const res = await markReadRequest({ ids });
        if (aliveRef.current) setUnread(res.unread);
      } catch {
        // Put the truth back rather than leaving a lie on screen.
        refresh();
      }
    },
    [authed, refresh]
  );

  const markAllRead = useCallback(async () => {
    if (!authed) return;
    const stamp = new Date().toISOString();
    setNotifications(current =>
      current.map(n => (n.read_at ? n : { ...n, read_at: stamp }))
    );
    setUnread(0);
    try {
      const res = await markReadRequest({ all: true });
      if (aliveRef.current) setUnread(res.unread);
    } catch {
      refresh();
    }
  }, [authed, refresh]);

  // ---------------------------------------------------------------------
  // The socket
  // ---------------------------------------------------------------------
  useEffect(() => {
    aliveRef.current = true;

    if (!authed) {
      // Signed out: drop the socket and the state with it. Leaving either would
      // show the previous occupant's notifications to whoever signs in next on
      // a shared front-desk terminal.
      if (socketRef.current) {
        socketRef.current.close(1000, 'signed out');
        socketRef.current = null;
      }
      setNotifications([]);
      setUnread(0);
      setNextCursor(null);
      setConnected(false);

      return () => {
        aliveRef.current = false;
      };
    }

    let closedByUs = false;

    const scheduleReconnect = () => {
      if (closedByUs || !aliveRef.current) return;
      const delay = retryRef.current;
      retryRef.current = Math.min(retryRef.current * 2, RECONNECT_MAX_MS);
      timerRef.current = window.setTimeout(connect, delay);
    };

    function connect() {
      if (!aliveRef.current || closedByUs) return;

      let ws: WebSocket;
      try {
        ws = new WebSocket(notificationSocketUrl());
      } catch {
        scheduleReconnect();

        return;
      }
      socketRef.current = ws;

      ws.onopen = () => {
        if (!aliveRef.current) return;
        retryRef.current = RECONNECT_MIN_MS;
        setConnected(true);
        // A reconnect usually means the tab was asleep, so it MISSED frames.
        // Re-reading the list is what makes the bell correct again rather than
        // merely live.
        refresh();
      };

      ws.onmessage = event => {
        if (!aliveRef.current) return;
        let frame: { type?: string; unread?: number; data?: AppNotification };
        try {
          frame = JSON.parse(event.data);
        } catch {
          return;
        }

        // Lobbero suspended or restored this organisation a moment ago. The
        // frame decides nothing: it only makes this tab ask GET /auth now, and
        // the answer swaps the app for the "suspended" screen or back.
        if (frame.type === 'org.status') {
          refreshAuthRef.current();

          return;
        }

        if (frame.type === 'hello' && typeof frame.unread === 'number') {
          setUnread(frame.unread);

          return;
        }

        if (frame.type === 'notification' && frame.data) {
          const incoming = frame.data;
          setNotifications(current =>
            current.some(n => n.id === incoming.id)
              ? current
              : [incoming, ...current]
          );
          setUnread(count => count + 1);
          // One line, and only for things worth interrupting for. A toast for
          // every roster change would train people to dismiss the one that says
          // a payment failed.
          if (incoming.severity !== 'info') {
            toast(incoming.title, { icon: incoming.severity === 'critical' ? '!' : '•' });
          }
          listenersRef.current.forEach(fn => {
            try {
              fn(incoming);
            } catch {
              // A listener's failure never breaks the bell.
            }
          });
        }
      };

      ws.onclose = () => {
        if (!aliveRef.current) return;
        setConnected(false);
        socketRef.current = null;
        scheduleReconnect();
      };

      // 'error' is ALWAYS followed by 'close', so the reconnect is scheduled
      // there and not here — doing it in both would double every backoff step
      // and turn a flapping connection into a request storm. This handler
      // exists only so an error does not surface as an unhandled event.
      ws.onerror = () => {
        setConnected(false);
      };
    }

    refresh();
    connect();

    return () => {
      aliveRef.current = false;
      closedByUs = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (socketRef.current) {
        socketRef.current.close(1000, 'unmounted');
        socketRef.current = null;
      }
    };
  }, [authed, refresh]);

  // A cheap correction for the case the socket cannot cover: a phone that was
  // locked with the tab suspended, where the socket died silently and the OS
  // froze our timers too. Cheap because it is one indexed COUNT(*).
  useEffect(() => {
    if (!authed) return undefined;

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      fetchUnreadCount()
        .then(count => {
          if (aliveRef.current) setUnread(count);
        })
        .catch(() => {
          // Ambient: a badge that could not refresh keeps its last value rather
          // than putting an error in front of somebody who just unlocked their
          // phone.
        });
    };

    document.addEventListener('visibilitychange', onVisible);

    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [authed]);

  const value = useMemo(
    () => ({
      notifications,
      unread,
      loading,
      nextCursor,
      connected,
      refresh,
      loadMore,
      markRead,
      markAllRead,
      subscribe,
    }),
    [
      subscribe,
      notifications,
      unread,
      loading,
      nextCursor,
      connected,
      refresh,
      loadMore,
      markRead,
      markAllRead,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
